import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Sparkles, ArrowRight, Zap, Infinity as InfinityIcon } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import FAQ from "./FAQ";
import PricingLocationSlider from "./PricingLocationSlider";
import PricingPlanCard from "./PricingPlanCard";
import PricingAddons from "./PricingAddons";
import PricingToolsGrid from "./PricingToolsGrid";
import PricingComparisonTable from "./PricingComparisonTable";
import SEO, { SITE_URL } from "../SEO";
import { Reveal, Overline } from "../Reveal";
import CurrencySelector from "../CurrencySelector";
import {
  PLANS,
  COMPARISON_SECTIONS,
  CHANNEL_PLANS,
  CHANNEL_COMPARISON_SECTIONS,
  planPath,
} from "../../data/content";
import { CurrencyProvider } from "../../contexts/CurrencyContext";

// Product/Offer JSON-LD per plan so an answer engine can quote each price
// correctly. Both levels expose yearly and one-time (lifetime) Offers -
// the same figures the plan cards and channel calculator display.
const offer = (name, price) => ({
  "@type": "Offer",
  name,
  price,
  priceCurrency: "INR",
  availability: "https://schema.org/InStock",
  url: `${SITE_URL}/pricing`,
});

const productNode = (p, offers) => ({
  "@type": "Product",
  name: `MagicBolt AI - ${p.name} Plan`,
  description: p.tagline,
  brand: { "@type": "Brand", name: "MagicBolt AI" },
  url: `${SITE_URL}${planPath(p)}`,
  offers,
});

const PLANS_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    ...PLANS.map((p) =>
      productNode(p, [
        offer("Yearly plan", p.pricing.yearly.amount),
        offer("One-time cost", p.pricing.onetime.amount),
      ])
    ),
    ...CHANNEL_PLANS.map((p) =>
      productNode(p, [
        offer("Yearly plan", p.pricing.yearly.amount),
        offer("Lifetime (one-time cost)", p.pricing.onetime.amount),
      ])
    ),
  ],
};

const LEVELS = [
  { key: "channel", label: "Channel Based", icon: Zap },
  { key: "unlimited", label: "Unlimited", icon: InfinityIcon },
];

// Payment views for the Unlimited plans - `key` matches a key in each
// plan's `pricing` object. `onetime` reads as "Lifetime": pay once, own it.
const UNLIMITED_VIEWS = [
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Annual" },
  { key: "onetime", label: "Lifetime" },
];

function LevelTabs({ level, onChange }) {
  return (
    <div role="tablist" aria-label="Pricing level" className="inline-flex items-center gap-1 rounded-full bg-[#F1F3F9] p-1.5">
      {LEVELS.map((l) => {
        const active = level === l.key;
        const Icon = l.icon;
        return (
          <button
            key={l.key}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`pricing-level-tab-${l.key}`}
            onClick={() => onChange(l.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
              active
                ? "bg-gradient-to-r from-[#FF6B00] to-[#FF8533] text-white shadow-[0_6px_18px_rgba(255,107,0,0.4)]"
                : "text-[#64748B] hover:text-[#0B1533]"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

function UnlimitedViewTabs({ view, onChange }) {
  return (
    <div role="tablist" aria-label="Unlimited pricing view" className="inline-flex items-center gap-1 rounded-full border border-[#E6E9F1] bg-white p-1 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
      {UNLIMITED_VIEWS.map((v) => {
        const active = view === v.key;
        const isLifetime = v.key === "onetime";
        return (
          <button
            key={v.key}
            role="tab"
            type="button"
            aria-selected={active}
            data-testid={`pricing-view-tab-${v.key}`}
            onClick={() => onChange(v.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm transition-all ${
              isLifetime ? "font-extrabold" : "font-semibold"
            } ${active ? "bg-[#FF6B00] text-white shadow-[0_6px_16px_rgba(255,107,0,0.35)]" : "text-[#475569] hover:text-[#0B1533]"}`}
          >
            {isLifetime && <Sparkles className={`mb-sparkle-icon w-3.5 h-3.5 ${active ? "text-white" : "text-[#FF6B00]"}`} />}
            {isLifetime && !active ? <span className="mb-gradient-text">{v.label}</span> : v.label}
          </button>
        );
      })}
    </div>
  );
}

// Maps between the `pricing` URL param and each level's internal view key.
const UNLIMITED_VIEW_TO_URL = { monthly: "monthly", yearly: "yearly", onetime: "lifetime" };
const URL_TO_UNLIMITED_VIEW = { monthly: "monthly", yearly: "yearly", lifetime: "onetime" };

function PricingPageContent() {
  // /pricing?plan=unlimited&pricing=lifetime&currency=AED - the level, the
  // active billing view within it, and the currency all read from and write
  // back to the URL so the exact state can be shared or bookmarked.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsLifetime = searchParams.get("pricing") === "lifetime";
  const urlPlan = searchParams.get("plan");

  const [level, setLevelState] = useState(
    urlPlan === "unlimited" || urlPlan === "channel" ? urlPlan : wantsLifetime ? "unlimited" : "channel"
  );
  const [unlimitedView, setUnlimitedViewState] = useState(() => {
    const fromUrl = URL_TO_UNLIMITED_VIEW[searchParams.get("pricing")];
    if (fromUrl && level === "unlimited") return fromUrl;
    return wantsLifetime ? "onetime" : "monthly";
  });
  const isChannel = level === "channel";
  const activePlans = isChannel ? CHANNEL_PLANS : PLANS;
  const activeSections = isChannel ? CHANNEL_COMPARISON_SECTIONS : COMPARISON_SECTIONS;

  const setLevel = (next) => {
    setLevelState(next);
    const params = new URLSearchParams(searchParams);
    params.set("plan", next);
    setSearchParams(params, { replace: true });
  };

  const setUnlimitedView = (next) => {
    setUnlimitedViewState(next);
    const params = new URLSearchParams(searchParams);
    params.set("plan", "unlimited");
    params.set("pricing", UNLIMITED_VIEW_TO_URL[next]);
    setSearchParams(params, { replace: true });
  };

  // Channel Based billing (Annual/Lifetime) lives inside PricingLocationSlider,
  // which remounts on every tab switch - this keeps its choice in the URL so
  // the remount picks the same billing back up via `wantsLifetime` below.
  const setChannelBilling = (billing) => {
    const params = new URLSearchParams(searchParams);
    params.set("plan", "channel");
    params.set("pricing", billing === "lifetime" ? "lifetime" : "yearly");
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="pricing-page">
      <SEO
        title="Pricing - White-Label AI Automation Platform | MagicBolt AI"
        description="Launch your own white-label QR software and AI automation platform under your brand. One-time platform setup, transparent platform renewal."
        path="/pricing"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Pricing", path: "/pricing" },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(PLANS_JSON_LD)}</script>
      </Helmet>
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="mb-blob mb-blob-blue mb-anim-b w-[320px] h-[320px] top-10 -left-20" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">One Setup. No Monthly Software Rental.</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              Grow 10x your Agency with{" "}
              <span className="mb-gradient-text">AI White-Label Solution</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
              Pay once to launch your own AI software and AI automation platform - then keep everything you charge your clients.
            </p>
            <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#FF6B00]/10 px-4 py-1.5 text-sm font-bold text-[#FF6B00]">
              Automate and Rank on Each Platform
            </p>
            <img
              src="/magicbolt-logo-transparent.webp"
              alt="MagicBolt AI"
              width="720"
              height="240"
              className="mt-4 mx-auto h-20 md:h-28 w-auto max-w-full"
            />
            <img
              src="/social%20icon.webp"
              alt="Automate and rank across Google Business, Facebook, Instagram, WhatsApp, WordPress and more"
              className="mt-2 mx-auto h-16 md:h-24 w-auto max-w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Plans */}
      <section id="pricing-section" data-testid="pricing-section" className="relative pt-10 pb-2 md:pt-12 md:pb-3 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <Overline>{isChannel ? "Simple, Channel-Based Pricing" : "Unlimited, One Login"}</Overline>
            <p className="mt-4 text-xl md:text-2xl font-bold text-[#0B1533] leading-snug">
              White Label AI SaaS For Resellers
            </p>
          </Reveal>

          <Reveal className="mt-6 flex justify-center">
            <LevelTabs level={level} onChange={setLevel} />
          </Reveal>
        </div>
      </section>

      {isChannel ? (
        <>
          <PricingLocationSlider
            initialBilling={wantsLifetime ? "lifetime" : "yearly"}
            onBillingChange={setChannelBilling}
          />
        </>
      ) : (
        <section className="relative pt-6 pb-4">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
            <Reveal className="flex flex-wrap items-center justify-center gap-3">
              <UnlimitedViewTabs view={unlimitedView} onChange={setUnlimitedView} />
              <CurrencySelector />
            </Reveal>

            <div className="mt-10 grid gap-6 lg:grid-cols-3 items-stretch">
              {PLANS.map((p, i) => (
                <Reveal key={p.key} delay={i * 0.1} className="flex flex-col">
                  <PricingPlanCard plan={p} view={unlimitedView} showDetails />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
        <Reveal className="mt-4 flex justify-center">
          <Link
            to="/how-agencies-earn"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B1533] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0B1533]/90 transition-colors"
          >
            See How Agencies Earn
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>

      <PricingComparisonTable
        plans={activePlans}
        sections={activeSections}
        view={isChannel ? (wantsLifetime ? "onetime" : "yearly") : unlimitedView}
      />
      <PricingAddons />
      <PricingToolsGrid />

      <FAQ />
      <CredibilityStrip />
      <Footer />
    </div>
  );
}

export default function PricingPage() {
  return (
    <CurrencyProvider>
      <PricingPageContent />
    </CurrencyProvider>
  );
}
